import { makeStyles } from '@map/theme';
import { ButtonBase, ButtonBaseProps, ButtonBaseTypeMap, ExtendButtonBase } from '@mui/material';
import { useFormatMessage } from '@shared/hooks/intl';
import { typePreservingForwardRef } from '@shared/util/preserveForwardRef';
import Button from '@ui/Button';
import ArrowBackIcon from '@ui/Icon/library/ArrowBackIcon';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import ChevronForwardIcon from '@ui/Icon/library/ChevronForwardIcon';
import ChevronUpIcon from '@ui/Icon/library/ChevronUpIcon';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import { DEV } from 'esm-env';
import { ComponentProps, ElementType, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, LinkProps } from 'react-router-dom';

type Variants =
  | 'appbar-back'
  | 'appbar-close'
  | 'bottombar-action'
  | 'bottombar-cancel'
  | 'see-more'
  | 'see-less'
  | 'unstyled'
  | 'view-all';

const useStyles = makeStyles(({ palette, spacing }) => ({
  bottombarCancel: {
    borderRight: `1px solid ${palette.neutrals[300]}`,
    minWidth: spacing(29),
    padding: spacing(4.5, 6),
  },
  action: {
    borderRadius: 0,
    minWidth: spacing(29),
    height: 'inherit',
    padding: spacing(4.5, 6),
  },
}));

type CustomProps = ComponentProps<typeof Button> & {
  // if `to` is supplied, we treat the button as a Link, otherwise there should be an onClick
  to?: LinkProps['to'] | number;
  replace?: LinkProps['replace'];
  which?: Variants;
  // only valid when the base component is an anchor
  target?: string;
};

export type NavButtonProps<TComponent extends ElementType = 'button'> = ButtonBaseProps<
  TComponent,
  CustomProps
>;

const NavButton: ExtendButtonBase<ButtonBaseTypeMap<NavButtonProps>> = ((
  props: ButtonBaseTypeMap<NavButtonProps>['props'] & {
    className?: string;
    component?: ElementType;
  },
  ref: RefObject<HTMLButtonElement>,
) => {
  const { which, ...otherProps } = props;
  const t = useFormatMessage();
  const { classes, cx } = useStyles();
  const { to } = otherProps;

  if (typeof to === 'number') {
    delete otherProps.to;
    otherProps.onClick = (e) => {
      history.go(to);
      props.onClick?.(e);
    };
  }
  // the TS definitions don't permit us to use the proper type
  const ButtonComponent: any = otherProps.to ? Link : otherProps.component;
  if (!otherProps.to) {
    delete otherProps.replace;
  }

  switch (which) {
    case 'appbar-back':
      return (
        <Button
          {...otherProps}
          variant="tertiary"
          size="medium"
          icon={true}
          component={ButtonComponent}
          ariaLabelIntlKey="page_nav.previous_button"
          className={otherProps.className}
          ref={ref}
        >
          <ArrowBackIcon />
        </Button>
      );
    case 'appbar-close':
      return (
        <Button
          {...otherProps}
          variant="tertiary"
          size="medium"
          icon={true}
          component={ButtonComponent}
          ariaLabelIntlKey="page_nav.cancel_button"
          ref={ref}
        >
          <CloseIcon />
        </Button>
      );
    case 'bottombar-cancel':
      return (
        <Button
          {...otherProps}
          size="medium"
          variant="tertiary"
          component={ButtonComponent}
          className={cx(classes.bottombarCancel, otherProps.className)}
          ref={ref}
        >
          <FormattedMessage id="page_nav.cancel_button" />
        </Button>
      );
    case 'bottombar-action':
      return (
        <Button
          {...otherProps}
          size="medium"
          component={ButtonComponent}
          className={cx(classes.action, otherProps.className)}
          ref={ref}
        />
      );
    case 'see-more':
    case 'see-less':
      return (
        <Button
          component={ButtonComponent}
          fullWidth={true}
          endIcon={which === 'see-more' ? <ChevronDownIcon /> : <ChevronUpIcon />}
          ref={ref}
          {...otherProps}
        >
          {which === 'see-more' ? (
            <FormattedMessage id="common.see_more" />
          ) : (
            <FormattedMessage id="common.see_less" />
          )}
        </Button>
      );
    case 'unstyled': {
      const { ariaLabelIntlKey, ...rest } = otherProps;
      return (
        <ButtonBase
          disableRipple={true}
          aria-label={ariaLabelIntlKey ? t(ariaLabelIntlKey) : undefined}
          {...rest}
          className={otherProps.className}
          component={ButtonComponent}
          ref={ref}
        />
      );
    }
    case 'view-all':
      return (
        <Button
          endIcon={<ChevronForwardIcon fontSize="small" />}
          variant="tertiary"
          {...otherProps}
          component={ButtonComponent}
          className={otherProps.className}
          ref={ref}
        >
          {otherProps.children || <FormattedMessage id="typeahead_list.view_all" />}
        </Button>
      );
  }

  return (
    <Button
      {...otherProps}
      className={otherProps.className}
      component={ButtonComponent}
      ref={ref}
    />
  );
}) as any; // Nasty solution to make TypeScript happy

if (DEV) {
  (NavButton as any).displayName = 'NavButton';
}

export default typePreservingForwardRef(NavButton);
